<template>
  <div class="chart-style-buttons-container">
    <div class="chart-style-buttons" :class="{ 'manage-chart-style-buttons': isManagingChartStyleButtons }">
      <span
        class="chart-style-button-container chart-style-icon-button-container"
        v-show="!isAnnotating && savedChartsStore.chartStyleButtonChartStyles.length"
        ref="toggleManageChartStyleButtonsButtonContainer"
      >
        <button
          type="button"
          class="chart-style-button"
          :class="{ 'sc3-button-link-green': isManagingChartStyleButtons }"
          @click="toggleManageChartStyleButtons()"
          @contextmenu.prevent="toggleManageChartStyleButtons()"
        >
          <i v-show="isManagingChartStyleButtons" class="fas fa-check"></i>
          <i v-show="!isManagingChartStyleButtons" class="fas fa-cog"></i>
        </button>
        <span v-if="!isTouchDevice || isManagingChartStyleButtons" class="chart-style-button-tooltip">
          <span class="chart-style-button-name">{{ isManagingChartStyleButtons ? 'Done' : 'Manage StyleButtons'}}</span>
        </span>
      </span>
      <span
        class="chart-style-button-container"
        v-for="(chartStyle, chartStyleButtonIndex) in savedChartsStore.chartStyleButtonChartStyles"
        :key="chartStyle.recid"
        v-show="!isAnnotating"
      >
        <button
          type="button"
          class="chart-style-button"
          :class="{
            'applied-chart-style-button': chartStyle.recid === chartStore.recidOfChartStyleBeingViewedWithAllChangesSaved
          }"
          :style="{
            backgroundColor: chartStyle.chartStyleButtonBackgroundColorHexCode,
            borderColor: chartStyle.chartStyleButtonBackgroundColorHexCode,
            outlineColor: chartStyle.chartStyleButtonBackgroundColorHexCode,
            color: chartStyle.chartStyleButtonTextColorHexCode
          }"
          :id="`chart-style-button-${chartStyle.recid}`"
          @click="applyChartStyle(chartStyle)"
          @contextmenu.prevent="launchChartStyleButtonModal(chartStyle.recid)"
        >{{ chartStyle.chartStyleButtonLabel }}</button>
        <span v-if="!isTouchDevice || isManagingChartStyleButtons" class="chart-style-button-tooltip">
          <button
            type="button"
            :title="`Edit StyleButton #${chartStyleButtonIndex + 1}`"
            class="chart-style-button-name"
            :style="{
              backgroundColor: chartStyle.chartStyleButtonBackgroundColorHexCode,
              color: chartStyle.chartStyleButtonTextColorHexCode,
              borderColor: chartStyle.chartStyleButtonTextColorHexCode
            }"
            @click="launchChartStyleButtonModal(chartStyle.recid)"
          >
            <span v-sanitize-html="chartStyle.name"></span>
            <i v-if="isManagingChartStyleButtons" class="fas fa-fw fa-pencil-alt"></i>
          </button>
        </span>
      </span>
      <span class="chart-style-button-container chart-style-icon-button-container interactive-tour-container" v-show="!isAnnotating">
        <button
          type="button"
          class="chart-style-button"
          @click="launchChartStyleButtonModal()"
          @contextmenu.prevent="launchChartStyleButtonModal()"
          :disabled="savedChartsStore.chartStyleButtonChartStyles.length >= MAX_NUMBER_OF_CHART_STYLE_BUTTONS"
        ><i class="fas fa-plus"></i></button>
        <span v-if="!isTouchDevice || isManagingChartStyleButtons" class="chart-style-button-tooltip">
          <span class="chart-style-button-name">Add StyleButton</span>
        </span>
        <InteractiveTourWindow
          :page-number="6"
          :arrow-position="InteractiveTourTrianglePosition.LeftTop"
          :position="{
            left: (dimensions) => `calc(100% + ${dimensions.baseWidth}px)`,
            top: (dimensions) => `calc(50% - ${dimensions.altitude}px)`
          }"
        >
          <template v-slot:main-header>
            <span>Store your go-to chart templates as StyleButtons for quick and easy one-click access</span>
          </template>
          <template v-slot:explanation>
            <div>
              <p>
                Once you've saved a few ChartStyles to your account, you'll probably have a few go-to templates that you want to use more often than the rest.
                You can save those favorites as “StyleButtons” for one-click access to any chart template.
                You can even customize them with different colors and text labels to really distinguish one style from another.
              </p>
            </div>
          </template>
        </InteractiveTourWindow>
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted, onUnmounted } from 'vue';
  import { useChartStore } from '@/stores/chartStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { InteractiveTourTrianglePosition } from '@/composables/useInteractiveTour';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useChartStyleButtons } from '@/composables/useChartStyleButtons';
  import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
  import { useChartNotesV2 } from '@/composables/useChartNotesV2';
  import { useChartDimensions } from '@/composables/useChartDimensions';
  import { useModals } from '@/composables/useModals';
  import { useDocumentProperties } from '@/composables/useDocumentProperties';
  import InteractiveTourWindow from '@/components/page-skeleton/InteractiveTourWindow.vue';
  import { SimpleFrontendChartStyle } from '@/interfaces/chartStyle';

  const chartStore = useChartStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();
  const { MAX_NUMBER_OF_CHART_STYLE_BUTTONS } = useChartStyleButtons();
  const { loadSimpleFrontendChartStyle } = useLoadChartsAndChartStyles();
  const { isAnnotating } = useChartNotesV2();
  const { CHART_DIMENSION_CONSTANTS } = useChartDimensions();
  const { launchCreateAccountModal, launchChartStyleButtonModal } = useModals();
  const { isTouchDevice } = useDocumentProperties();
  const {
    CHART_STYLE_BUTTON_CONTAINER_WIDTH
  } = CHART_DIMENSION_CONSTANTS;
  const toggleManageChartStyleButtonsButtonContainer = ref<HTMLSpanElement>();
  const isManagingChartStyleButtons = ref(false);

  async function applyChartStyle(chartStyle: SimpleFrontendChartStyle) {
    const chartStyleButton = document.getElementById(`chart-style-button-${chartStyle.recid}`);

    chartStyleButton?.blur();

    if (
      !isLoggedIn.value &&
      chartStyle.isForPayingMembersOnly
    ) {
      launchCreateAccountModal();
      return;
    }

    await loadSimpleFrontendChartStyle(chartStyle, { shouldScrollToChartOnSuccess: false });
  }

  function toggleManageChartStyleButtons() {
    if (!savedChartsStore.chartStyleButtonChartStyles.length) {
      isManagingChartStyleButtons.value = false;
      return;
    }

    if (!isLoggedIn.value) {
      isManagingChartStyleButtons.value = false;

      launchCreateAccountModal();
      return;
    }

    isManagingChartStyleButtons.value = !isManagingChartStyleButtons.value;
  }

  function handleGlobalClick(event: MouseEvent) {
    if (
      !toggleManageChartStyleButtonsButtonContainer.value ||
      !(event.target instanceof Node)
    ) {
      isManagingChartStyleButtons.value = false;
      return;
    }

    if (
      toggleManageChartStyleButtonsButtonContainer.value === event.target ||
      toggleManageChartStyleButtonsButtonContainer.value.contains(event.target)
    ) {
      return;
    }

    isManagingChartStyleButtons.value = false;
  }

  onMounted(() => {
    document.body.addEventListener('click', handleGlobalClick);
    document.body.addEventListener('contextmenu', handleGlobalClick);
  });

  onUnmounted(() => {
    document.body.removeEventListener('click', handleGlobalClick);
    document.body.removeEventListener('contextmenu', handleGlobalClick);
  });
</script>

<style scoped>
  .chart-style-buttons-container {
    display: flex;
    width: v-bind(CHART_STYLE_BUTTON_CONTAINER_WIDTH + 'px');
    padding: 4px 0px 0px 0px;
  }

  .chart-style-buttons {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .chart-style-button-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
    flex-grow: 1;
    width: fit-content;
    min-height: var(--chart-style-button-tooltip-height);
    max-height: 24px;
  }

  .chart-style-button,
  .chart-style-button:hover,
  .chart-style-button:active,
  .chart-style-button:focus {
    width: v-bind(CHART_STYLE_BUTTON_CONTAINER_WIDTH + 'px');
    padding: 0px;
    margin: 0px;
    border-width: 2px;
    border-style: solid;
    border-radius: 2px;
    font-size: 10px;
    overflow: hidden;
    font-family: monospace, 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 700;
  }

  .chart-style-icon-button-container {
    flex-grow: unset;
    min-height: unset;
    max-height: unset;
  }

  .chart-style-icon-button-container .chart-style-button,
  .chart-style-icon-button-container .chart-style-button:hover,
  .chart-style-icon-button-container .chart-style-button:active,
  .chart-style-icon-button-container .chart-style-button:focus {
    background: transparent;
    border: none;
    font-size: 14px;
    height: var(--chart-style-button-tooltip-height);
  }

  .applied-chart-style-button,
  .applied-chart-style-button:hover,
  .applied-chart-style-button:active,
  .applied-chart-style-button:focus {
    outline-width: 2px;
    outline-style: solid;
    outline-offset: 1px;
  }

  .chart-style-button-tooltip {
    display: none;
    margin-left: 5px;
    white-space: nowrap;
    z-index: 995;
    height: var(--chart-style-button-tooltip-height);
  }

  .chart-style-button-name {
    display: inline-flex;
    gap: 4px;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    font-weight: 700;
    padding: 0px 6px;
    border-radius: 3px;
    white-space: nowrap;
    line-height: 1;
    margin-left: 1px;
    border-width: 1px;
    border-style: solid;
    height: 100%;
  }

  .chart-style-icon-button-container .chart-style-button + .chart-style-button-tooltip .chart-style-button-name {
    background-color: #a9b2bc;
    color: white;
    border-color: white;
  }

  .chart-style-button:hover + .chart-style-button-tooltip,
  .manage-chart-style-buttons .chart-style-button-container:not(.chart-style-icon-button-container) .chart-style-button-tooltip {
    display: inline-flex;
    align-self: center;
  }

  .manage-chart-style-buttons {
    background-color: rgba(242, 245, 247, 0.9);
    z-index: 1;
    height: fit-content;
    border: 1px solid #707f8f;
    border-left: none;
    border-radius: 0px 5px 5px 0px;
    padding: 0px 5px 0px 0px;
    box-shadow: 0 0.25rem 0.5rem rgba(0,0,0,0.35);
  }
</style>
